import { useMutation, useQuery } from "@tanstack/react-query";
import { api, isTemporaryError, retryLoadDelay } from "../lib/apiClient";

/** Downloads the full database — every table's rows, plus the actual bytes
 * of every attachment and avatar they reference — as one zip file. Super
 * Admin only, enforced server-side regardless of what's shown here. */
export interface BackupProgress {
  /** Bytes received so far (0 while the server is still preparing). */
  received: number;
  /** The zip's size in bytes. */
  total: number;
}

/** Request 1297 — everything, the records alone, or only the files new since the last backup that had files. */
export type BackupKind = "FULL" | "DATA" | "FILES";
const FILE_PREFIX: Record<BackupKind, string> = { FULL: "qplus-backup", DATA: "qplus-data", FILES: "qplus-files" };

interface BackupJobStatus {
  id: string;
  kind: BackupKind;
  status: "BUILDING" | "READY" | "FAILED";
  size: number;
  fileCount: number;
  message: string | null;
}

export interface BackupRun {
  snapshotAt: string;
  fileCount: number;
  bytes: number;
  byName: string | null;
}
export function useBackupRuns() {
  return useQuery({ queryKey: ["backup-runs"], queryFn: async () => (await api.get<{ data: Record<BackupKind, BackupRun | null> }>("/backup/runs")).data.data });
}

const PART_BYTES = 8 * 1024 * 1024;
const PART_TRIES = 8;
const PART_TIMEOUT_MS = 2 * 60 * 1000;
const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

/** Keeps asking through a dropped connection or a busy server; a real refusal, or a backup the server no longer has, stops it. */
async function withRetries<T>(attempt: () => Promise<T>): Promise<T> {
  for (let tries = 0; ; tries++) {
    try {
      return await attempt();
    } catch (err) {
      if (tries + 1 >= PART_TRIES || !(isTemporaryError(err) || err instanceof ShortPart)) throw err;
      await wait(retryLoadDelay(tries));
    }
  }
}
class ShortPart extends Error {}

/** Request 1294 — the server builds the zip first, then it comes down in small pieces, so a dropped connection costs one piece rather than the whole backup. */
export async function downloadBackup(kind: BackupKind, onProgress?: (p: BackupProgress) => void): Promise<{ nothingNew: boolean }> {
  const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
  onProgress?.({ received: 0, total: 0 });
  let job = (await api.post<{ data: BackupJobStatus }>("/backup/jobs", { kind })).data.data;
  while (job.status === "BUILDING") {
    await wait(2000);
    const id = job.id;
    job = await withRetries(async () => (await api.get<{ data: BackupJobStatus }>(`/backup/jobs/${id}/status`)).data.data);
  }
  if (job.status === "FAILED") throw new Error(job.message ?? "The backup could not be built.");
  if (job.kind === "FILES" && job.fileCount === 0) {
    await api.post(`/backup/jobs/${job.id}/done`).catch(() => undefined);
    return { nothingNew: true };
  }

  const total = job.size;
  const parts: Blob[] = [];
  for (let start = 0; start < total; start += PART_BYTES) {
    const end = Math.min(start + PART_BYTES, total) - 1;
    const expected = end - start + 1;
    parts.push(
      await withRetries(async () => {
        const res = await api.get<Blob>(`/backup/jobs/${job.id}/part`, {
          params: { start, end },
          responseType: "blob",
          timeout: PART_TIMEOUT_MS,
          onDownloadProgress: (e) => onProgress?.({ received: start + e.loaded, total }),
        });
        if (res.data.size !== expected) throw new ShortPart("Part of the backup arrived incomplete.");
        return res.data;
      })
    );
  }
  const zip = new Blob(parts, { type: "application/zip" });
  if (zip.size !== total) throw new Error("The backup arrived incomplete. Please try again.");
  api.post(`/backup/jobs/${job.id}/done`).catch(() => undefined); // otherwise it's cleared after two hours
  const url = window.URL.createObjectURL(zip);
  const link = document.createElement("a");
  link.href = url;
  link.setAttribute("download", `${FILE_PREFIX[job.kind]}-${stamp}.zip`);
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
  return { nothingNew: false };
}

export interface RestoreBackupResult {
  restoredModels: string[];
  skippedUnknownModels: string[];
  restoredFiles: number;
  missingFiles: number;
  filesOnly: boolean;
}

/** Wipes every table the file covers and reloads it verbatim — a full
 * replace, not a merge. `confirmText` must be the literal string "RESTORE",
 * checked again server-side, so this can't fire by accident. */
export function useRestoreBackup() {
  return useMutation({
    mutationFn: async ({ file, confirmText }: { file: File; confirmText: string }) => {
      const form = new FormData();
      form.append("file", file);
      form.append("confirmText", confirmText);
      return (await api.post<{ data: RestoreBackupResult }>("/backup/import", form, { headers: { "Content-Type": "multipart/form-data" } })).data.data;
    },
  });
}

export interface IdCleanupChange {
  kind: "Estimation" | "Project";
  label: string;
  before: string;
  after: string;
}
export interface IdCleanupConflict {
  kind: "Estimation" | "Project";
  label: string;
  before: string;
  wouldBecome: string;
}
export interface IdCleanupResult {
  changes: IdCleanupChange[];
  conflicts: IdCleanupConflict[];
}

/** One-off tool: previews (does not write) the Estimation/Project IDs a
 * bulk import left with a stray space in them — see cleanUpImportedIds on
 * the backend for the full story. Not auto-run; only fetches when asked. */
export function usePreviewIdCleanup(enabled: boolean) {
  return useQuery({
    queryKey: ["id-cleanup-preview"],
    queryFn: async () => (await api.get<{ data: IdCleanupResult }>("/backup/fix-imported-ids")).data.data,
    enabled,
  });
}

export function useApplyIdCleanup() {
  return useMutation({
    mutationFn: async () => (await api.post<{ data: IdCleanupResult }>("/backup/fix-imported-ids")).data.data,
  });
}

export interface ProjectStageMigrationChange {
  boardId: string;
  name: string;
  stageName: string;
}
export interface ProjectStageMigrationResult {
  changes: ProjectStageMigrationChange[];
}

/** One-off tool: sets a sensible initial Projects-page Stage for projects
 * that predate request 1005 (still unset/"Backlog" by default) based on
 * whether their own tasks are actually done — see migrateProjectStages on
 * the backend for the full story. Not auto-run; only fetches when asked. */
export function usePreviewProjectStageMigration(enabled: boolean) {
  return useQuery({
    queryKey: ["project-stage-migration-preview"],
    queryFn: async () => (await api.get<{ data: ProjectStageMigrationResult }>("/backup/migrate-project-stages")).data.data,
    enabled,
  });
}

export function useApplyProjectStageMigration() {
  return useMutation({
    mutationFn: async () => (await api.post<{ data: ProjectStageMigrationResult }>("/backup/migrate-project-stages")).data.data,
  });
}

export interface ProjectCustomerReconcileChange {
  boardId: string;
  boardName: string;
  boardCustomerBefore: string | null;
  taskCustomerName: string | null;
}
export interface ProjectCustomerReconcileResult {
  changes: ProjectCustomerReconcileChange[];
}

/** One-off tool: a project's own customer (Projects list, Accounts, Board
 * Settings, ...) is copied from its anchor task's customer once, at award
 * time, and wasn't editable on the task itself until this fix shipped — so
 * any project whose anchor task's customer was since changed some other way
 * is still showing a stale customer everywhere but the task. This makes the
 * board match its anchor task. See reconcileProjectCustomers on the backend
 * for the full story. Not auto-run; only fetches when asked. */
export function usePreviewProjectCustomerReconcile(enabled: boolean) {
  return useQuery({
    queryKey: ["project-customer-reconcile-preview"],
    queryFn: async () => (await api.get<{ data: ProjectCustomerReconcileResult }>("/backup/reconcile-project-customers")).data.data,
    enabled,
  });
}

export function useApplyProjectCustomerReconcile() {
  return useMutation({
    mutationFn: async () => (await api.post<{ data: ProjectCustomerReconcileResult }>("/backup/reconcile-project-customers")).data.data,
  });
}
